import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { API_BASE_URL } from '../config/api';
import { authFetch } from '../utils/authFetch';
import { useSessionStorage } from '../hooks/useSessionStorage';

// Notification type from API
export interface Notification {
  id: number;
  recipientId: number;
  recipientType: 'CLIENT' | 'CONTRACTOR';
  type: string;
  title: string;
  message: string;
  entityType?: string;
  entityId?: number;
  isRead: boolean;
  readAt?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

// Filter types
export interface NotificationFilters {
  showUnreadOnly: boolean;
  types: NotificationCategory[]; // empty = all types
}

export type NotificationCategory = 'bookings' | 'invoices' | 'messages';

export interface FilterCounts {
  bookings: number;
  invoices: number;
  messages: number;
}

// Map notification types to UI categories
// Note: Jobs and Bookings share notification types, combined into 'bookings' category
const TYPE_TO_CATEGORY: Record<string, NotificationCategory> = {
  // Bookings category (includes jobs since they share types)
  BOOKING_REQUESTED: 'bookings',
  BOOKING_CONFIRMED: 'bookings',
  BOOKING_CANCELLED: 'bookings',
  BOOKING_REMINDER: 'bookings',
  TIME_OPTIONS_PROPOSED: 'bookings',
  TIME_OPTION_SELECTED: 'bookings',
  TIME_OPTIONS_DECLINED: 'bookings',
  RESCHEDULE_PROPOSED: 'bookings',
  RESCHEDULE_ACCEPTED: 'bookings',
  RESCHEDULE_DECLINED: 'bookings',
  // Invoices category
  INVOICE_SENT: 'invoices',
  PAYMENT_RECEIVED: 'invoices',
  PAYMENT_FAILED: 'invoices',
  // Messages category
  NEW_MESSAGE: 'messages',
  // System (show with bookings)
  WELCOME: 'bookings',
  ACCOUNT_UPDATE: 'bookings',
};

function getCategory(type: string): NotificationCategory {
  return TYPE_TO_CATEGORY[type] || 'bookings';
}

const DEFAULT_FILTERS: NotificationFilters = {
  showUnreadOnly: false,
  types: [],
};

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refresh: () => Promise<void>;
  // Filter state
  filters: NotificationFilters;
  setFilters: (filters: NotificationFilters) => void;
  clearFilters: () => void;
  filteredNotifications: Notification[];
  filterCounts: FilterCounts;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const POLL_INTERVAL = 30000; // 30 seconds

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, userType, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Filter state with sessionStorage persistence
  const [filters, setFilters] = useSessionStorage<NotificationFilters>(
    'notification-filters',
    DEFAULT_FILTERS
  );

  // Clear filters to defaults
  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, [setFilters]);

  // Compute filter counts for each category
  const filterCounts = useMemo<FilterCounts>(() => {
    const counts: FilterCounts = { bookings: 0, invoices: 0, messages: 0 };
    for (const notification of notifications) {
      const category = getCategory(notification.type);
      counts[category]++;
    }
    return counts;
  }, [notifications]);

  // Compute filtered notifications
  const filteredNotifications = useMemo(() => {
    let result = notifications;

    // Apply read/unread filter
    if (filters.showUnreadOnly) {
      result = result.filter(n => !n.isRead);
    }

    // Apply type filter (if any types selected, empty = show all)
    if (filters.types.length > 0) {
      result = result.filter(n => {
        const category = getCategory(n.type);
        return filters.types.includes(category);
      });
    }

    return result;
  }, [notifications, filters]);

  // Get recipient info from auth context
  const getRecipientInfo = useCallback(() => {
    if (!user || !userType) return null;
    return {
      recipientId: user.id,
      recipientType: userType.toUpperCase() as 'CLIENT' | 'CONTRACTOR',
    };
  }, [user, userType]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    const recipient = getRecipientInfo();
    if (!recipient) return;

    try {
      const response = await authFetch(
        `${API_BASE_URL}/notifications/${recipient.recipientType}/${recipient.recipientId}?limit=50`
      );
      const data = await response.json();
      if (data.success) {
        setNotifications(data.notifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [getRecipientInfo]);

  // Fetch unread count (lighter query for polling)
  // When count changes, also refresh the full notification list
  const fetchUnreadCount = useCallback(async () => {
    const recipient = getRecipientInfo();
    if (!recipient) return;

    try {
      const response = await authFetch(
        `${API_BASE_URL}/notifications/${recipient.recipientType}/${recipient.recipientId}/count`
      );
      const data = await response.json();
      if (data.success) {
        const newCount = data.unread;
        setUnreadCount(prev => {
          if (prev !== newCount) {
            // Count changed — fetch the full notification list
            fetchNotifications();
          }
          return newCount;
        });
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [getRecipientInfo, fetchNotifications]);

  // Combined refresh
  const refresh = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([fetchNotifications(), fetchUnreadCount()]);
    setIsLoading(false);
  }, [fetchNotifications, fetchUnreadCount]);

  // Mark single notification as read (optimistic update)
  const markAsRead = useCallback(async (id: number) => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    const recipient = getRecipientInfo();
    if (!recipient) return;

    try {
      await authFetch(`${API_BASE_URL}/notifications/${id}/read`, {
        method: 'PATCH',
      });
    } catch (error) {
      // Revert on failure
      console.error('Error marking notification as read:', error);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, isRead: false, readAt: undefined } : n)
      );
      setUnreadCount(prev => prev + 1);
    }
  }, [getRecipientInfo]);

  // Mark all as read (optimistic update)
  const markAllAsRead = useCallback(async () => {
    const previousNotifications = notifications;
    const previousCount = unreadCount;

    // Optimistic update
    setNotifications(prev =>
      prev.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
    );
    setUnreadCount(0);

    const recipient = getRecipientInfo();
    if (!recipient) return;

    try {
      await authFetch(
        `${API_BASE_URL}/notifications/${recipient.recipientType}/${recipient.recipientId}/read-all`,
        { method: 'POST' }
      );
    } catch (error) {
      // Revert on failure
      console.error('Error marking all as read:', error);
      setNotifications(previousNotifications);
      setUnreadCount(previousCount);
    }
  }, [notifications, unreadCount, getRecipientInfo]);

  // Initial fetch on mount and when user changes
  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated, refresh]);

  // Polling with visibility API
  useEffect(() => {
    if (!isAuthenticated) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      // Immediate fetch on visibility change to visible
      fetchUnreadCount();
      intervalId = setInterval(fetchUnreadCount, POLL_INTERVAL);
    };

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startPolling();
      } else {
        stopPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start polling if tab is currently visible
    if (document.visibilityState === 'visible') {
      startPolling();
    }

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, fetchUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    refresh,
    // Filter state
    filters,
    setFilters,
    clearFilters,
    filteredNotifications,
    filterCounts,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
